import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../utils/supabaseClient";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

interface ProductRow {
  name: string;
  size: string;
  price: number;
}

const parseRow = (row: Record<string, unknown>): ProductRow[] => {
  // Normalize keys to lowercase so "NAME", "Size", "PRICE" all resolve correctly
  const n: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) n[k.toLowerCase()] = v;
  // When the name column has no header, xlsx assigns the key "__empty"
  const names = String(n["name"] ?? n["__empty"] ?? "").split(/\r?\n/);
  const sizes = String(n["size"] ?? "").split(/\r?\n/);
  const prices = String(n["price"] ?? "0").split(/\r?\n/);
  const count = Math.max(names.length, sizes.length, prices.length);
  return Array.from({ length: count }, (_, i) => ({
    name: (names[i] ?? names[names.length - 1]).toLowerCase().trim(),
    size: (sizes[i] ?? sizes[sizes.length - 1]).trim(),
    price:
      Number(
        (prices[i] ?? prices[prices.length - 1]).replace(/[^0-9.]/g, "")
      ) || 0,
  })).filter((r) => r.name && r.size && r.price > 0);
};

const AddProductsFromExcelSheet = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ProductRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
      setPreview(rows.flatMap(parseRow));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!preview.length) return;
    setUploading(true);
    setResult(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/api/v1/products/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify(preview),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setResult({ ok: false, message: err.detail ?? "Upload failed." });
      } else {
        const data = await res.json();
        setResult({
          ok: true,
          message: `${data.inserted} product${data.inserted !== 1 ? "s" : ""} added to the database.`,
        });
        setPreview([]);
        setFileName("");
        if (inputRef.current) inputRef.current.value = "";
      }
    } catch {
      setResult({ ok: false, message: "Could not reach the server." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 3,
        }}
      >
        Upload Products
      </Typography>

      <Box
        sx={{
          border: "1px dashed #d4d4d4",
          p: 4,
          textAlign: "center",
          mb: 3,
          cursor: "pointer",
          "&:hover": { borderColor: "black" },
        }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          data-testid="file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <Typography variant="body2" sx={{ color: "#737373", mb: 1 }}>
          {fileName || "Click to select an Excel or CSV file"}
        </Typography>
        <Typography variant="caption" sx={{ color: "#a3a3a3" }}>
          Accepts .xlsx · .xls · .csv
        </Typography>
      </Box>

      {result && (
        <Alert
          severity={result.ok ? "success" : "error"}
          sx={{ mb: 3, borderRadius: 0 }}
        >
          {result.message}
        </Alert>
      )}

      {preview.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{
              color: "#737373",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              display: "block",
              mb: 2,
            }}
          >
            Preview — {preview.length} row{preview.length !== 1 ? "s" : ""}{" "}
            detected
          </Typography>

          <Box sx={{ overflowX: "auto", mb: 3, border: "1px solid #e5e5e5" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  {["Name", "Size", "Price"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((row, i) => (
                  <TableRow key={i} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.size}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      ${row.price}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              disabled={uploading}
              onClick={handleUpload}
              sx={{
                bgcolor: "black",
                color: "white",
                px: 4,
                py: 1.5,
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                borderRadius: 0,
                "&:hover": { bgcolor: "#333" },
              }}
            >
              {uploading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                `Save ${preview.length} Product${preview.length !== 1 ? "s" : ""} to Database`
              )}
            </Button>
            <Button
              variant="outlined"
              disabled={uploading}
              onClick={() => {
                setPreview([]);
                setFileName("");
                setResult(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              sx={{
                borderColor: "#d4d4d4",
                color: "#737373",
                px: 4,
                py: 1.5,
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                borderRadius: 0,
                "&:hover": { borderColor: "black", color: "black" },
              }}
            >
              Discard
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AddProductsFromExcelSheet;
